type SiteLabelPopout = "normal" | "inline" | "none" | "always";
type SiteLabelScroll = "top" | "view";

interface ISiteLabelConfig {
    popout: SiteLabelPopout,
    scroll: SiteLabelScroll
}

interface ISiteFocusable
    extends IQueryElement {
    makeActive(): boolean;
    makeInactive(): void;
    makePrevActive(): void;
    makeNextActive(): void;
}

interface ISiteLabel
    extends ISiteFocusable {
    init(input?: IQueryElement): void;

    getInput(): IQueryElement | null;
    setError(error: string): void;
    clearError(): void;

    path: string[];
}

abstract class SiteLabel<T, TConfig extends ISiteLabelConfig>
    extends QueryElement<HTMLElement>
    implements ISiteLabel {

    private readonly _card: SiteCard;
    private readonly _model: ISiteModel;
    private readonly _modelData: ISiteModel;
    private readonly _path: string[];
    private readonly _config: TConfig;
    static skipCtrlKeyCodes = [
        67, // CTRL + C
        86, // CTRL + V
        88, // CTRL + X
        65  // CTRL + A
    ];

    constructor(card: SiteCard, model: ISiteModel, modelData: ISiteModel, element: HTMLElement) {
        super(element);

        this.setAttr("if-ISiteLabel", "true");
        this.setAttr("if-ISiteFocusable", "true");

        let path = this.getAttr('data-path');

        if(path === null)
            throw 'Labels must have a path!';

        this._card = card;
        this._model = model;
        this._modelData = modelData;
        this._path = SiteModel.split(path);
        this._config = this.getConfig();

        this.on("click", () => {
            card.makeFocusableActive(this) 
        });

    }
    init(input?: IQueryElement) {
        let value = this.getModelValue();
        if (value === undefined)
            this.initialValue(null);   
        else
            this.initialValue(value);

        if (input === undefined)
            return;
        
        input.on("keydown", (e: KeyboardEvent) => this.defaultKeyDownHandler(e));
        input.on("focus", () => {
            this._card.makeFocusableActive(this) 
        });
        
        var controlMode = this.getControlMode();
        if (controlMode != null) {
            if (controlMode == "Hide") {
                this.addClass("hide");
                this.setAttr("if-ISiteFocusable", "false");
            }
        }
    }
    defaultKeyDownHandler(e: KeyboardEvent) {
        switch(e.keyCode) {
            default:
                if (e.ctrlKey && !SiteLabel.skipCtrlKeyCodes.includes(e.keyCode))
                {
                    this._card.keyPress([17, e.keyCode]);
                    e.stop();
                }
                break;
            case 17: // CTRL
                this._card.keyPress([e.keyCode]);
                e.stop();
                break;
            case 13: // ENTER
                if (!e.ctrlKey && !e.shiftKey && !e.altKey) {
                    this._card.keyPress([e.keyCode]);
                    e.stop();
                }
                break;
            case 9: // TAB
                if(e.shiftKey)
                    this.makePrevActive(true);
                else
                    this.makeNextActive(true);
                e.stop();
                break;
        }
    }
    get config() : TConfig {
        return this._config;
    }
    get path() {
        return this._path;
    }
    abstract defaultConfig() : TConfig;
    abstract getValue(): T | null;
    abstract getInput(): IQueryElement | null;

    isNotNull(value: T | null): boolean {
        return value !== null;
    }
    private initialValue(value: T | null) {
        this._model.set(this._path, this, value, true);
        this.setValue(value);
    }
    setValue(value: T | null) {
        if(this.isNotNull(value))
            this.addClass("not-null");
        else
            this.removeClass("not-null");
        this._model.set(this._path, this, value, false);
    }

    setError(error: string) {
        this.setAttr("data-error", error);
        this.addClass("error");
    }
    clearError() {
        this.setAttr("data-error", "");
        this.removeClass("error");
    }
    makeCurrentActive() {
        this._card.makeFocusableActive(this);
    }
    makePrevActive(force = false) {
        if (!this._card.SiteConfig.settings.includes("NoAutoTab") || force) {
            this._card.makePrevFocusableActive(this);
            return true;
        }
        return false;
    }
    makeNextActive(force = false) {
        if (!this._card.SiteConfig.settings.includes("NoAutoTab") || force) {
            this._card.makeNextFocusableActive(this);
            return true;
        }
        return false;
    }
    getSiteConfig() {
        return this._card.SiteConfig;
    }
    getCard() {
        return this._card;
    }
    getIcon() {
        return this.query("i.inline.collapse").first();
    }
    getModelValue() : T | undefined {
        let value = this._model
                .get(this._path);
        
        return value;
    }
    getModelData(): any | undefined {
        let value = this._modelData
            .get(this._path.concat("info"));

        return value;
    }
    getControlMode(): ControlMode | undefined {
        let value = this._modelData
            .get(this._path.concat("controlMode"));
        
        
        return value;
    }
    setModelData(modelData: any | undefined) {
        this._modelData.set(
            this._path.concat("info"),
            this,
            modelData,
            false
        );
    }
    private getConfig() {
        let defaultConfig = this.defaultConfig();
        let dataConfig = this.getAttr('data-config') || "";
        
        let config : TConfig | null = null;
        if(dataConfig.startsWith("{"))
            config = QueryJSON.deserialize<TConfig>(dataConfig);
        else if(dataConfig)
            config = this._model.get(SiteModel.split(dataConfig)) as TConfig;
        
        if (config != null)
            return {...defaultConfig, ...config};
        else
            return defaultConfig;
    }

    makeActive() {
        if(this.containsClass("active"))
            return false;

        // Scroll into position:
        let content = this._card.query(".content")
            .first<QueryElement<HTMLElement>>();

        let elem = content && content.get();
        if (content !== null && elem !== null) {
            let posFocusable = this.position();
            let posContent = content.position();
            if (this.config.scroll == "top") {
                let toPositionY = (posFocusable && posFocusable.top || 0)
                    - (posContent && posContent.top || 0);

                content.scrollToY(
                    elem.scrollTop + toPositionY,
                    400,
                    Easing.easeOutCubic);
            } else if (this.config.scroll == "view") {
                // Browser will do this
            }
        }
        
        this.addClass("active").focus();
        this.trigger("qts.active", false);

        return true;
    }
    makeInactive() {
        this.removeClass("active");
        this.setValue(this.getValue());
        this.trigger("qts.inactive", false);
    }
}